import React, { useState } from 'react';
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Image,
  Link,
  useColorModeValue,
  useToast,
  Icon,
  Circle,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { MdCheck, MdBusiness, MdDescription } from 'react-icons/md';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Logo from '../../../assets/img/auth/Logo.png';
import CompanySignupApi from '../../../api/companySignup';


// Import step components
import { ContactDetailsStep, CommercialTermsStep, SuccessStep } from './steps';

// Validation schemas for each step
const step1Schema = Yup.object({
  fullName: Yup.string()
    .min(2, 'יש להזין לפחות 2 תווים')
    .max(50, 'שם ארוך מדי')
    .required('שם מלא הוא שדה חובה'),
  email: Yup.string()
    .required('כתובת אימייל היא שדה חובה')
    .email('כתובת אימייל לא תקינה')
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      'כתובת אימייל לא תקינה (לדוגמה: user@example.com)'
    ),
  companyId: Yup.string()
    .required('מספר החברה הוא שדה חובה')
    .matches(/^\d{9}$/, 'מספר החברה חייב להכיל 9 ספרות'),
  contactPhone: Yup.string()
    .required('טלפון איש הקשר הוא שדה חובה')
    .matches(/^05\d{8}$/, 'מספר הטלפון חייב להתחיל ב-05 ולהכיל 10 ספרות'),
  companyName: Yup.string()
    .nullable()
});

const step2Schema = Yup.object({
  termsApproved: Yup.boolean()
    .oneOf([true], 'יש לאשר את התנאים המסחריים ותנאי השימוש')
});

const CompanySignupWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Company Registration/Employer Details
    fullName: '',
    email: '',
    companyId: '',
    contactPhone: '',
    companyName: '',

    // Step 2: Commercial Terms
    termsApproved: false,
  });

  const toast = useToast();
  const { isOpen: isErrorModalOpen, onOpen: onErrorModalOpen, onClose: onErrorModalClose } = useDisclosure();
  const [errorMessage, setErrorMessage] = useState('');
  const [companySignupData, setCompanySignupData] = useState(null);

  // Color mode values
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = useColorModeValue('gray.400', 'white');

  const steps = [
    { number: 1, title: 'פרטי המעסיק', icon: MdBusiness },
    { number: 2, title: 'תנאים מסחריים', icon: MdDescription },
    { number: 3, title: 'השלמה', icon: MdCheck },
  ];

  const getValidationSchema = () => {
    switch (currentStep) {
      case 1: return step1Schema;
      case 2: return step2Schema;
      case 3: return Yup.object({}); // No validation needed for success step
      default: return Yup.object({});
    }
  };

  const handleNext = async (values, { setSubmitting }) => {
    const updatedData = { ...formData, ...values };
    setFormData(updatedData);

    if (currentStep === 1) {
      // Step 1: Validate company exists and contact phone matches
      try {
        const validationResult = await CompanySignupApi.validateCompanyAndPhone(
          updatedData.companyId,
          updatedData.contactPhone
        );

        if (!validationResult.success) {
          setErrorMessage(validationResult.errorMessage || 'שגיאה בבדיקת פרטי החברה. אנא נסה שוב');
          onErrorModalOpen();
          setSubmitting(false);
          return;
        }

        // If validation successful, fetch company data for step 2
        const companyDataResult = await CompanySignupApi.getCompanySignupData(updatedData.companyId);
        if (companyDataResult.success) {
          setCompanySignupData(companyDataResult.data);
        }

        setCurrentStep(2);
      } catch (error) {
        console.error('Error validating company:', error);
        setErrorMessage('שגיאה בבדיקת פרטי החברה. אנא נסה שוב');
        onErrorModalOpen();
        setSubmitting(false);
        return;
      }
    } else if (currentStep === 2) {
      // Final submission after commercial terms approval
      await handleFinalSubmit(updatedData);
      setCurrentStep(3); // Move to success step
    }
    setSubmitting(false);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinalSubmit = async (finalData) => {
    try {
      console.log('Final submission data:', finalData);

      // Prepare update data with contact details from the form
      const updateData = {
        contactName: finalData.fullName,
        contactEmail: finalData.email
      };

      // Update approve terms and contact details in the backend
      const approveResult = await CompanySignupApi.approveTerms(finalData.companyId, updateData);

      if (!approveResult.success) {
        throw new Error(approveResult.errorMessage || 'שגיאה באישור התנאים');
      }

      toast({
        title: 'רישום החברה הושלם בהצלחה!',
        description: 'נציג מטעם Payro ייצור איתכם קשר בקרוב',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });

      // Don't reset form data here - we need it for the success page
      // The success page will fetch fresh data from the database

    } catch (error) {
      console.error('Error in final submission:', error);
      toast({
        title: 'שגיאה ברישום',
        description: error.message || 'אירעה שגיאה בעת רישום החברה. אנא נסו שוב.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const renderStepContent = (values, errors, touched, setFieldValue, validateForm) => {
    switch (currentStep) {
      case 1:
        return (
          <ContactDetailsStep
            data={values}
            updateData={async (updates) => {
              Object.keys(updates).forEach(key => {
                setFieldValue(key, updates[key]);
              });
              // Trigger validation after updating fields
              setTimeout(() => validateForm(), 0);
            }}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
          />
        );
      case 2:
        return (
          <CommercialTermsStep
            data={values}
            companySignupData={companySignupData}
            updateData={(updates) => {
              Object.keys(updates).forEach(key => {
                setFieldValue(key, updates[key]);
              });
            }}
            errors={errors}
            touched={touched}
          />
        );
      case 3:
        return (
          <SuccessStep
            data={values}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box minH="100vh" bg="teal.50" dir="rtl" display="flex" flexDirection="column" justifyContent="center" alignItems="center" p={{ base: 4, md: 8 }}>
      <Box>
      <Box
        maxW={{ base: '90%', sm: '500px', md: '600px', lg: '700px' }}
        w="full"
        bg="white"
        boxShadow="xl"
        borderRadius="20px"
        overflow="hidden"
      >
        {/* Header with Steps Indicator */}
        <Box bg="teal.50" p={{ base: 6, md: 8 }} textAlign="center">
          <Image
            src={Logo}
            alt="Payro Logo"
            h={{ base: '40px', md: '50px' }}
            w="auto"
            mb={6}
            mx="auto"
          />
          
          {/* Steps Progress */}
          <HStack spacing={4} justify="center" mb={4}>
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <VStack spacing={2}>
                  <Circle
                    size="50px"
                    bg={
                      step.number < currentStep
                        ? 'teal.500'
                        : step.number === currentStep
                        ? 'teal.500'
                        : 'gray.200'
                    }
                    color="white"
                    transition="all 0.3s"
                  >
                    {step.number < currentStep ? (
                      <Icon as={MdCheck} boxSize={6} />
                    ) : (
                      <Icon as={step.icon} boxSize={6} />
                    )}
                  </Circle>
                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    color={
                      step.number <= currentStep ? textColor : textColorSecondary
                    }
                  >
                    {step.title}
                  </Text>
                </VStack>
                {index < steps.length - 1 && (
                  <Box
                    w="40px"
                    h="2px"
                    bg={
                      step.number < currentStep ? 'teal.500' : 'gray.200'
                    }
                    transition="all 0.3s"
                  />
                )}
              </React.Fragment>
            ))}
          </HStack>
        </Box>

        {/* Form Content */}
        <Box p={{ base: 6, md: 8 }}>
          <Formik
            initialValues={formData}
            validationSchema={getValidationSchema()}
            onSubmit={handleNext}
            enableReinitialize
            validateOnChange={true}
            validateOnBlur={true}
          >
            {({ values, errors, touched, isSubmitting, setFieldValue, isValid, validateField, validateForm }) => (
              <Form>
                <VStack spacing={6}>
                  {renderStepContent(values, errors, touched, setFieldValue, validateForm)}
                  
                  {/* Navigation Buttons - Hide on success step */}
                  {currentStep < 3 && (
                    <HStack spacing={4} w="full" justify="space-between">
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={handleBack}
                        isDisabled={currentStep === 1}
                        borderColor="teal.500"
                        color="teal.500"
                        _hover={{ bg: 'teal.50' }}
                      >
                        חזור
                      </Button>

                      <Button
                        type="submit"
                        size="lg"
                        bg="teal.500"
                        color="white"
                        _hover={{ bg: 'teal.600' }}
                        _active={{ bg: 'teal.700' }}
                        isLoading={isSubmitting}
                        loadingText={currentStep === 2 ? 'מעבד...' : 'ממשיך...'}
                        isDisabled={
                          !isValid ||
                          isSubmitting ||
                          (currentStep === 1 && (
                            !values.fullName?.trim() ||
                            !values.email?.trim() ||
                            !values.companyId?.trim() ||
                            !values.contactPhone?.trim() ||
                            errors.fullName ||
                            errors.email ||
                            errors.companyId ||
                            errors.contactPhone
                          )) ||
                          (currentStep === 2 && !values.termsApproved)
                        }
                      >
                        {currentStep === 2 ? 'אישור וסיום' : 'המשך'}
                      </Button>
                    </HStack>
                  )}
                </VStack>
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
      </Box>

      {/* Support Footer - Very close to the form */}
      <Box textAlign="center" mt={4} px={6}>
        <Text fontSize="14px" color="gray.500">
          זקוק/ה לעזרה? צור קשר:{' '}
          <Link
            href="mailto:support@payro.io"
            color="teal.500"
            textDecoration="underline"
            _hover={{ color: 'teal.600' }}
          >
            support@payro.io
          </Link>
          {' | '}050-7717556
        </Text>
      </Box>

      {/* Error Modal */}
      <Modal isOpen={isErrorModalOpen} onClose={onErrorModalClose} isCentered>
        <ModalOverlay />
        <ModalContent mx={4} dir="rtl">
          <ModalHeader textAlign="center" color="red.500">
            שגיאה
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody textAlign="center" pb={6}>
            <Text>{errorMessage}</Text>
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button colorScheme="red" onClick={onErrorModalClose}>
              סגור
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CompanySignupWizard;
